import React, {useEffect, useRef, useState} from 'react';
import {Controller, useForm} from 'react-hook-form';
import {IFarmer, IFdUser, ISupplierUserProfile} from 'constants/interfaces';
import {
  Autocomplete as GooglePlacesAutocomplete,
  useJsApiLoader,
} from '@react-google-maps/api';
import {GOOGLE_API_KEY} from 'constants/index';
import {
  apiGetFdUserByEmail,
  apiUpdateFdCompany,
  apiUpdateFdFarm,
  apiUpdateFdFarmFinancials,
  apiUpdateFdUser,
} from 'constants/Api';
import {
  apiUpdateInputsCompany,
  apiUpdateInputsFarm,
  apiUpdateInputsUser,
} from 'constants/api/farmers';
import {
  Box,
  Card,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  TextField,
  Tooltip,
  Typography,
} from '@material-ui/core';
import EditIcon from '@material-ui/icons/Edit';
import CloseIcon from '@material-ui/icons/Close';
import SaveIcon from '@material-ui/icons/Save';
import {Skeleton} from '@material-ui/lab';

import './FarmerCRMCard.scss';

export interface FarmerCRMFormData {
  // Farm details → updateFDFarm
  farmName: string;
  farmProvince: string;
  farmAddress: string;
  ownedHectares: string;
  // Username / Contact → updateFDUser
  firstName: string;
  lastName: string;
  phone: string;
  email: string;
  // Company name & Address → updateFDCompany
  companyName: string;
  companyAddress: string;
  // VAT & Registration — Farmer (customer) → updateFDFarmFinancials
  farmerVatNumber: string;
  farmerTaxNumber: string;
  // VAT & Registration — Khula (supplier) — read-only display
  supplierVatNumber: string;
  supplierTaxNumber: string;
}

export function buildDefaultValues(
  farmer?: IFarmer,
  fdUser?: IFdUser | null,
  user?: ISupplierUserProfile,
): FarmerCRMFormData {
  return {
    farmName: fdUser?.farm?.name ?? farmer?.farm?.name ?? '',
    farmProvince: fdUser?.farm?.province ?? farmer?.farm?.province ?? '',
    farmAddress: fdUser?.farm?.address ?? farmer?.address ?? '',
    ownedHectares:
      fdUser?.farm?.ownedHectares != null
        ? String(fdUser.farm.ownedHectares)
        : farmer?.farm?.sizeHectares ?? '',

    firstName: fdUser?.name ?? farmer?.name ?? '',
    lastName: fdUser?.surname ?? farmer?.surname ?? '',
    phone: fdUser?.phoneNumber ?? farmer?.phone ?? '',
    email: fdUser?.email ?? farmer?.email ?? '',

    companyName: fdUser?.company?.name ?? '',
    companyAddress: fdUser?.company?.address ?? '',

    farmerVatNumber: fdUser?.financials?.vatNumber ?? '',
    farmerTaxNumber: fdUser?.financials?.taxNumber ?? '',

    supplierVatNumber: user?.company?.vatNumber ?? '',
    supplierTaxNumber: user?.company?.taxNumber ?? '',
  };
}

export function FarmerCRMCard(props: {
  farmer?: IFarmer;
  user: ISupplierUserProfile;
  isLoading?: boolean;
  onSave?: () => void;
}) {
  const {farmer, user, isLoading, onSave} = props;

  const [isEditing, setIsEditing] = useState(false);
  const [isFetchingFdUser, setIsFetchingFdUser] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [fdUser, setFdUser] = useState<IFdUser | null>(null);
  const [noFdProfile, setNoFdProfile] = useState(false);

  const {handleSubmit, control, reset} = useForm<FarmerCRMFormData>({
    defaultValues: buildDefaultValues(farmer, null, user),
  });

  const resolveFdUser = async (email: string): Promise<IFdUser | null> => {
    return apiGetFdUserByEmail(email);
  };

  useEffect(() => {
    if (!farmer?.email) return;
    resolveFdUser(farmer.email)
      .then((fetchedFdUser) => {
        setFdUser(fetchedFdUser);
        setNoFdProfile(!fetchedFdUser);
      })
      .catch(() => setNoFdProfile(true));
  }, [farmer?.email]);

  const handleEditOpen = () => {
    if (!farmer?.email) {
      reset(buildDefaultValues(farmer, null, user));
      setIsEditing(true);
      return;
    }
    setIsFetchingFdUser(true);
    resolveFdUser(farmer.email)
      .then((fetchedFdUser) => {
        setFdUser(fetchedFdUser);
        setNoFdProfile(!fetchedFdUser);
        reset(buildDefaultValues(farmer, fetchedFdUser, user));
        setIsEditing(true);
      })
      .catch((err: unknown) => {
        console.error('[FarmerCRMCard] resolveFdUser error:', err);
        setNoFdProfile(true);
        reset(buildDefaultValues(farmer, null, user));
        setIsEditing(true);
      })
      .finally(() => setIsFetchingFdUser(false));
  };

  const handleCancelEdit = () => {
    reset(buildDefaultValues(farmer, fdUser, user));
    setIsEditing(false);
  };

  const onSubmit = (data: FarmerCRMFormData) => {
    const fdUserId = fdUser?.id ? Number(fdUser.id) : undefined;
    const fdCompanyId = fdUser?.company?.id
      ? Number(fdUser.company.id)
      : undefined;
    const fdFarmId = fdUser?.farm?.id ? Number(fdUser.farm.id) : undefined;

    console.log('[FarmerCRMCard] onSubmit — fdUser:', fdUser, 'ids:', {
      fdUserId,
      fdCompanyId,
      fdFarmId,
    });

    if (!fdUserId && !fdFarmId && !fdCompanyId) {
      alert(
        'No funder-dashboard profile found for this farmer. Changes cannot be saved.',
      );
      setIsEditing(false);
      return;
    }

    const promises: Promise<any>[] = [];

    if (fdUserId) {
      promises.push(
        apiUpdateFdUser({
          id: fdUserId,
          name: data.firstName || undefined,
          surname: data.lastName || undefined,
          phoneNumber: data.phone || undefined,
        }),
      );
    }

    if (fdFarmId || fdUserId) {
      promises.push(
        apiUpdateFdFarm({
          farmId: fdFarmId,
          applyingUserId: fdFarmId ? undefined : fdUserId,
          name: data.farmName || undefined,
          province: data.farmProvince || undefined,
          address: data.farmAddress || undefined,
          ownedHectares: data.ownedHectares
            ? Number(data.ownedHectares)
            : undefined,
        }),
      );
    }

    if (fdCompanyId) {
      promises.push(
        apiUpdateFdCompany({
          id: fdCompanyId,
          name: data.companyName || undefined,
          address: data.companyAddress || undefined,
        }),
      );
      if (data.farmerVatNumber || data.farmerTaxNumber) {
        promises.push(
          apiUpdateFdFarmFinancials({
            companyId: fdCompanyId,
            vatNumber: data.farmerVatNumber || undefined,
            taxNumber: data.farmerTaxNumber || undefined,
          }),
        );
      }
    }

    if (farmer?.userId) {
      promises.push(
        apiUpdateInputsUser(farmer.userId, {
          name: data.firstName || undefined,
          surname: data.lastName || undefined,
          phone: data.phone || undefined,
        }).catch((err) =>
          console.warn('[CRMCard] inputs user update failed:', err),
        ),
      );
      promises.push(
        apiUpdateInputsFarm(farmer.userId, {
          farmName: data.farmName || undefined,
          province: data.farmProvince || undefined,
          address: data.farmAddress || undefined,
          ownedHectares: data.ownedHectares
            ? Number(data.ownedHectares)
            : undefined,
        }).catch((err) =>
          console.warn('[CRMCard] inputs farm update failed:', err),
        ),
      );
      promises.push(
        apiUpdateInputsCompany(farmer.userId, {
          name: data.companyName || undefined,
          location: data.companyAddress || undefined,
          vatNumber: data.farmerVatNumber || undefined,
          taxNumber: data.farmerTaxNumber || undefined,
        }).catch((err) =>
          console.warn('[CRMCard] inputs company update failed:', err),
        ),
      );
    }

    setIsSaving(true);
    Promise.all(promises)
      .then(() => {
        setFdUser((prev) =>
          prev
            ? {
                ...prev,
                name: data.firstName || prev.name,
                surname: data.lastName || prev.surname,
                phoneNumber: data.phone || prev.phoneNumber,
                farm: prev.farm
                  ? {
                      ...prev.farm,
                      name: data.farmName || prev.farm.name,
                      province: data.farmProvince || prev.farm.province,
                      address: data.farmAddress || prev.farm.address,
                      ownedHectares: data.ownedHectares
                        ? Number(data.ownedHectares)
                        : prev.farm.ownedHectares,
                    }
                  : prev.farm,
                company: prev.company
                  ? {
                      ...prev.company,
                      name: data.companyName || prev.company.name,
                      address: data.companyAddress || prev.company.address,
                    }
                  : prev.company,
              }
            : null,
        );
        setIsEditing(false);
        onSave?.();
      })
      .catch((err) => {
        console.error('Failed to save farmer CRM data', err);
        alert('Failed to save changes. Please try again.');
      })
      .finally(() => setIsSaving(false));
  };

  if (isLoading) {
    return <Skeleton variant="rect" height={240} style={{borderRadius: 8}} />;
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="farmer-crm-card">
      <Box className="farmer-crm-header">
        <Typography variant="subtitle1">Farm Details</Typography>
        {isEditing ? (
          <Box display="flex" style={{gap: 4}}>
            <Tooltip title="Cancel">
              <span>
                <IconButton
                  aria-label="Cancel edit"
                  size="small"
                  onClick={handleCancelEdit}
                  disabled={isSaving}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Save changes">
              <span>
                <IconButton
                  aria-label="Save changes"
                  size="small"
                  type="submit"
                  color="primary"
                  disabled={isSaving}
                >
                  {isSaving ? (
                    <CircularProgress size={16} thickness={5} />
                  ) : (
                    <SaveIcon fontSize="small" />
                  )}
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        ) : (
          <Tooltip
            title={
              user.isKhulaAdmin
                ? 'Edit farmer details'
                : 'Only Khula admins can edit farmer details'
            }
          >
            <span>
              <IconButton
                aria-label="Edit farmer details"
                size="small"
                onClick={handleEditOpen}
                disabled={!farmer || isFetchingFdUser || !user.isKhulaAdmin}
              >
                {isFetchingFdUser ? (
                  <CircularProgress size={16} thickness={5} />
                ) : (
                  <EditIcon fontSize="small" />
                )}
              </IconButton>
            </span>
          </Tooltip>
        )}
      </Box>

      {isEditing ? (
        <EditForm control={control} noFdProfile={noFdProfile} />
      ) : (
        <ViewCard farmer={farmer} user={user} fdUser={fdUser} />
      )}
    </form>
  );
}

/* ── View mode ─────────────────────────────────────────────────────────── */

function ViewCard(props: {
  farmer?: IFarmer;
  user: ISupplierUserProfile;
  fdUser: IFdUser | null;
}) {
  const {farmer, user, fdUser} = props;
  if (!farmer) return null;

  const row = (label: string, value?: string | number | null) => (
    <>
      <Box className="row">
        <Box className="title-container">
          <Typography variant="subtitle2">{label}</Typography>
        </Box>
        <Box className="value-container">
          <Typography variant="body2" color="textSecondary">
            {value ?? '—'}
          </Typography>
        </Box>
      </Box>
      <Divider />
    </>
  );

  const sectionLabel = (label: string) => (
    <Box className="crm-section-label">
      <Typography variant="caption" color="textSecondary">
        {label}
      </Typography>
    </Box>
  );

  const farm = farmer.farm;

  return (
    <Card className="farmer-detail-card" variant="outlined">
      {sectionLabel('Farm Details')}
      {row('Farm', fdUser?.farm?.name ?? farm?.name)}
      {row('Province', fdUser?.farm?.province ?? farm?.province)}
      {row('Address', fdUser?.farm?.address ?? farmer.address)}
      {row(
        'Hectares',
        fdUser?.farm?.ownedHectares != null
          ? String(fdUser.farm.ownedHectares)
          : farm?.sizeHectares,
      )}
      {sectionLabel('Username / Contact')}
      {row('First Name', fdUser?.name ?? farmer.name)}
      {row('Last Name', fdUser?.surname ?? farmer.surname)}
      {row('Email', fdUser?.email ?? farmer.email)}
      {row('Phone', fdUser?.phoneNumber ?? farmer.phone)}

      {sectionLabel('Company & Address')}
      {row('Company Name', fdUser?.company?.name)}
      {row('Address', fdUser?.company?.address)}

      {sectionLabel('VAT & Registration — Farmer')}
      {row('VAT Number', fdUser?.financials?.vatNumber ?? '—')}
      {row('Tax Number', fdUser?.financials?.taxNumber ?? '—')}

      {sectionLabel('VAT & Registration — Khula')}
      {row('VAT Number', user.company?.vatNumber)}
      {row('Tax Number', user.company?.taxNumber)}
    </Card>
  );
}

/* ── Address autocomplete ───────────────────────────────────────────────── */

const MAPS_LIBS: 'places'[] = ['places'];

function AddressAutocompleteInput(props: {
  value: string;
  onChange: (v: string) => void;
  label: string;
  error?: boolean;
  helperText?: string;
  disabled?: boolean;
}) {
  const {value, onChange, label, error, helperText, disabled} = props;
  const {isLoaded} = useJsApiLoader({
    googleMapsApiKey: GOOGLE_API_KEY,
    libraries: MAPS_LIBS,
  });
  const acRef = useRef<google.maps.places.Autocomplete | null>(null);

  const textField = (
    <TextField
      label={label}
      variant="outlined"
      size="small"
      fullWidth
      value={value}
      onChange={(e) => onChange(e.target.value)}
      error={error}
      helperText={helperText}
      disabled={disabled}
    />
  );

  if (!isLoaded) return textField;

  return (
    <GooglePlacesAutocomplete
      onLoad={(ac) => {
        acRef.current = ac;
      }}
      onPlaceChanged={() => {
        const place = acRef.current?.getPlace();
        if (place?.formatted_address) onChange(place.formatted_address);
      }}
      options={{types: ['address'], componentRestrictions: {country: 'za'}}}
    >
      {textField}
    </GooglePlacesAutocomplete>
  );
}

/* ── Edit mode ─────────────────────────────────────────────────────────── */

function EditForm(props: {control: any; noFdProfile: boolean}) {
  const {control, noFdProfile} = props;

  const field = (
    name: keyof FarmerCRMFormData,
    label: string,
    disabled?: boolean,
  ) => (
    <Grid item xs={12} sm={6}>
      <Controller
        name={name}
        control={control}
        render={({field: {onChange, value}, fieldState: {error}}) => (
          <TextField
            label={label}
            variant="outlined"
            size="small"
            fullWidth
            value={value}
            onChange={onChange}
            error={!!error}
            helperText={error?.message}
            disabled={!!disabled}
          />
        )}
      />
    </Grid>
  );

  const addressField = (name: keyof FarmerCRMFormData, label: string) => (
    <Grid item xs={12} sm={6}>
      <Controller
        name={name}
        control={control}
        render={({field: {onChange, value}, fieldState: {error}}) => (
          <AddressAutocompleteInput
            value={value}
            onChange={onChange}
            label={label}
            error={!!error}
            helperText={error?.message}
          />
        )}
      />
    </Grid>
  );

  const sectionLabel = (label: string) => (
    <Grid item xs={12}>
      <Typography variant="subtitle2" className="crm-edit-section-label">
        {label}
      </Typography>
    </Grid>
  );

  return (
    <Card
      className="farmer-detail-card farmer-crm-edit-card"
      variant="outlined"
    >
      {noFdProfile && (
        <Box className="crm-no-profile-banner">
          <Typography variant="caption">
            No funder-dashboard profile found for this farmer. Company and VAT
            fields cannot be saved.
          </Typography>
        </Box>
      )}
      <Grid container spacing={2} className="crm-edit-grid">
        {sectionLabel('Farm Details')}
        {field('farmName', 'Farm Name')}
        {field('farmProvince', 'Province')}
        {addressField('farmAddress', 'Address')}
        {field('ownedHectares', 'Owned Hectares')}

        {sectionLabel('Username / Contact')}
        {field('firstName', 'First Name')}
        {field('lastName', 'Last Name')}
        {field('email', 'Email', true)}
        {field('phone', 'Phone')}

        {sectionLabel('Company Name & Address')}
        {field('companyName', 'Company Name')}
        {addressField('companyAddress', 'Address')}

        {sectionLabel('VAT & Registration — Farmer (Customer)')}
        {field('farmerVatNumber', 'VAT Number')}
        {field('farmerTaxNumber', 'Tax Number')}

        {sectionLabel('VAT & Registration — Khula (Supplier)')}
        {field('supplierVatNumber', 'VAT Number', true)}
        {field('supplierTaxNumber', 'Tax Number', true)}
      </Grid>
    </Card>
  );
}
